import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './hooks/useAuthStore';
import AppLayout from './components/layout/AppLayout';
import StubPage from './components/common/StubPage';
import LoginPage from './pages/LoginPage';
import ProjectSelectPage from './pages/ProjectSelectPage';
import DashboardPage from './pages/DashboardPage';
import FormationLayersPage from './pages/formation/FormationLayersPage';
import FormationProfilePage from './pages/formation/FormationProfilePage';
import Formation3DPage from './pages/formation/Formation3DPage';
import StructurePage from './pages/formation/StructurePage';
import FracRosePage from './pages/naturalfrac/FracRosePage';
import FracDensityPage from './pages/naturalfrac/FracDensityPage';
import FracTablePage from './pages/naturalfrac/FracTablePage';
import FormationTopsPage from './pages/formation/FormationTopsPage';
import MechProfilePage from './pages/mechanics/MechProfilePage';
import MechStressPage from './pages/mechanics/MechStressPage';
import PressureWindowPage from './pages/stability/PressureWindowPage';
import ImageLogPage from './pages/stability/ImageLogPage';
import RockMechanics from './pages/RockMechanics';
import SweetSpotPage from './pages/SweetSpotPage';
import SweetCurvesPage from './pages/sweetspot/SweetCurvesPage';
import FracturePage from './pages/FracturePage';
import FractureDesignsPage from './pages/fracture/FractureDesignsPage';
import LabExperimentPage from './pages/fracture/LabExperimentPage';
import PumpScheduleWorkspace from './pages/fracture/PumpScheduleWorkspace';
import FractureLibraryPage from './pages/fracture/FractureLibraryPage';
import PerforationStagePage from './pages/fracture/PerforationStagePage';
import MaterialDesignWorkspace from './pages/fracture/MaterialDesignWorkspace';
import FracSimPage from './pages/FracSimPage';
import FracSimHistoryPage from './pages/fracsim/FracSimHistoryPage';
import FracSimSensitivityPage from './pages/fracsim/FracSimSensitivityPage';
import FracStageDetailPage from './pages/fracsim/FracStageDetailPage';
import FracCurvesPage from './pages/fracsim/FracCurvesPage';
import EcoCostPage from './pages/economics/EcoCostPage';
import EcoProductionPage from './pages/economics/EcoProductionPage';
import EcoNpvPage from './pages/economics/EcoNpvPage';
import ProductionForecastPage from './pages/ProductionForecastPage';
import InversionPage from './pages/inversion/InversionPage';
import InversionStatsPage from './pages/inversion/InversionStatsPage';
import ControlOverviewPage from './pages/control/ControlOverviewPage';
import ControlMetricsPage from './pages/control/ControlMetricsPage';
import ControlTrainingPage from './pages/control/ControlTrainingPage';
import WarningPage from './pages/WarningPage';
import WarningRunsPage from './pages/warning/WarningRunsPage';
import WarningRunDetailPage from './pages/warning/WarningRunDetailPage';
import WarningEventsPage from './pages/warning/WarningEventsPage';
import WarningRulesPage from './pages/warning/WarningRulesPage';
import DataHubPage from './pages/datahub/DataHubPage';
import { GEOMECHANICS_UI_ENABLED } from './pages/geomechanics/feature';
import './styles/global.css';

const GeomechanicsWorkbenchPage = lazy(
  () => import('./pages/geomechanics/GeomechanicsWorkbenchPage'),
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Project selection (no sidebar) */}
        <Route path="/projects" element={
          <ProtectedRoute><ProjectSelectPage /></ProtectedRoute>
        } />

        {/* Main app with sidebar */}
        <Route path="/" element={
          <ProtectedRoute><AppLayout /></ProtectedRoute>
        }>
          <Route index element={<Navigate to="/projects" />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* ── 数据中心 ── */}
          <Route path="datahub">
            <Route index element={<Navigate to="/datahub/manage" replace />} />
            <Route path="import"  element={<DataHubPage />} />
            <Route path="process" element={<DataHubPage />} />
            <Route path="assets"  element={<DataHubPage />} />
            <Route path="manage"  element={<DataHubPage />} />
          </Route>

          {/* ── 单井测井：数据中心标准化结果的一级下游入口 ── */}
          <Route path="logging">
            <Route index element={<Navigate to="/logging/curves" replace />} />
            <Route path="curves" element={<SweetCurvesPage />} />
          </Route>

          {/* ── 地质建模 (原"地层绘制"): 地层格架 + 天然裂缝(/naturalfrac) + 构造解释 ── */}
          <Route path="formation">
            <Route index element={<Navigate to="/formation/profile" replace />} />
            <Route path="layers"  element={<FormationLayersPage />} />
            <Route path="profile" element={<FormationProfilePage />} />
            <Route path="3d"      element={<Formation3DPage />} />
            <Route path="tops"    element={<FormationTopsPage />} />
            <Route path="seismic" element={<StructurePage />} />
          </Route>

          {/* ── 岩石力学 ── */}
          <Route path="mechanics">
            <Route index element={<Navigate to="/mechanics/profile" replace />} />
            <Route path="profile" element={<MechProfilePage />} />
            <Route path="stress"  element={<MechStressPage />} />
            <Route path="table"   element={<Navigate to="/mechanics/profile" replace />} />
            <Route path="rock-mechanics" element={<RockMechanics />} />
            <Route
              path="workbench/:module?"
              element={GEOMECHANICS_UI_ENABLED ? (
                <Suspense fallback={<div style={{ padding: 40 }}>地质力学工作台加载中…</div>}>
                  <GeomechanicsWorkbenchPage />
                </Suspense>
              ) : <Navigate to="/mechanics/profile" replace />}
            />
          </Route>

          {/* ── 天然裂缝: 归属「地质建模」模块 (路径保持不变, 免得旧书签失效) ── */}
          <Route path="naturalfrac">
            <Route index element={<Navigate to="/naturalfrac/rose" replace />} />
            <Route path="rose"    element={<FracRosePage />} />
            <Route path="density" element={<FracDensityPage />} />
            <Route path="table"   element={<FracTablePage />} />
          </Route>

          {/* ── 井壁稳定 (二级结构, 逐步填实) ── */}
          <Route path="stability">
            <Route index element={<Navigate to="/stability/window" replace />} />
            <Route path="window" element={<PressureWindowPage mode="window" />} />
            <Route path="pressure" element={<PressureWindowPage mode="pressure" />} />
            <Route path="imaging"  element={<ImageLogPage />} />
            <Route path="params"   element={<StubPage title="强度参数" hint="UCS / 内聚力 / 内摩擦角 / 抗拉强度 等井壁稳定计算输入。后续填实。" />} />
          </Route>

          {/* ── 甜点评价 ── */}
          <Route path="sweetspot">
            <Route index element={<Navigate to="/sweetspot/results" replace />} />
            <Route path="results" element={<SweetSpotPage />} />
            {/* 兼容旧书签；测井曲线已升级为独立一级模块。 */}
            <Route path="curves"  element={<Navigate to="/logging/curves" replace />} />
            {/* 旧版独立公式/方法页统一回到 SSI 评价工作台，避免重复配置流程。 */}
            <Route path="calc"    element={<Navigate to="/sweetspot/results" replace />} />
            <Route path="methods" element={<Navigate to="/sweetspot/results" replace />} />
          </Route>

          {/* ── 压裂设计 ── */}
          <Route path="fracture">
            <Route index element={<Navigate to="/fracture/new-design" replace />} />
            <Route path="new-design" element={<FractureDesignsPage mode="create" />} />
            <Route path="designs"  element={<FractureDesignsPage />} />
            <Route path="lab"      element={<LabExperimentPage />} />
            <Route path="wellbore" element={<PerforationStagePage />} />
            <Route path="builder"  element={<FracturePage />} />
            <Route path="materials" element={<MaterialDesignWorkspace />} />
            <Route path="schedule" element={<PumpScheduleWorkspace />} />
            <Route path="library"  element={<FractureLibraryPage />} />
          </Route>

          {/* ── 压裂模拟 ── */}
          <Route path="fracsim">
            <Route index element={<Navigate to="/fracsim/overview" replace />} />
            <Route path="overview"    element={<FracSimPage />} />
            <Route path="curves"     element={<FracCurvesPage />} />
            <Route path="stage"       element={<FracStageDetailPage />} />
            <Route path="sensitivity" element={<FracSimSensitivityPage />} />
            <Route path="history"     element={<FracSimHistoryPage />} />
          </Route>

          {/* ── 裂缝反演 (微地震) ── */}
          <Route path="inversion">
            <Route index element={<Navigate to="/inversion/reconstruct" replace />} />
            <Route path="reconstruct" element={<InversionPage />} />
            <Route path="stats" element={<InversionStatsPage />} />
          </Route>

          {/* ── 自适应调控 (强化学习) ── */}
          <Route path="control">
            <Route index element={<Navigate to="/control/policy" replace />} />
            <Route path="policy"   element={<ControlOverviewPage />} />
            <Route path="metrics"  element={<ControlMetricsPage />} />
            <Route path="training" element={<ControlTrainingPage />} />
          </Route>

          {/* ── 工况预警 ── */}
          <Route path="warning">
            <Route index element={<Navigate to="/warning/runs" replace />} />
            <Route path="runs"     element={<WarningRunsPage />} />
            <Route path="runs/:id" element={<WarningRunDetailPage />} />
            <Route path="live"     element={<WarningPage />} />
            <Route path="events"   element={<WarningEventsPage />} />
            <Route path="rules"    element={<WarningRulesPage />} />
          </Route>

          {/* ── 压后评价: 已拆解并入其他模块, 仅保留重定向以免旧书签失效 ──
               生产动态 → 经济评价/产量预测;  微地震 → 裂缝反演;  压裂效果 → 裂缝反演/统计分析 */}
          <Route path="postfrac">
            <Route index element={<Navigate to="/economics/production" replace />} />
            <Route path="production"    element={<Navigate to="/economics/production" replace />} />
            <Route path="effect"        element={<Navigate to="/inversion/stats" replace />} />
            <Route path="microseismic"  element={<Navigate to="/inversion/reconstruct" replace />} />
          </Route>

          {/* ── 经济评价 ── */}
          <Route path="economics">
            <Route index element={<Navigate to="/economics/cost" replace />} />
            <Route path="cost"       element={<EcoCostPage />} />
            <Route path="production" element={<EcoProductionPage />} />
            <Route path="npv"        element={<EcoNpvPage />} />
          </Route>

          {/* — 产能预测 — */}
          <Route
            path="production-forecast"
            element={<ProductionForecastPage />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
